import I18n from 'I18n';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { useMemo } from 'react';
import { getScopeGroupI18nKey } from '../../utils/getScopeGroupI18nKey';
import { getAppObjectFQNs, getIsAppObjectAppPermission } from '../utils/appObjectAppPermission';
import { useFetchAppObjectNames } from './internal/get-app-objects';
import { useGetAppPermissionTranslations } from './internal/get-app-permission-translations';
import { getAppObjectAppPermissionDescription } from './internal/translate-app-object';
import { useGetAppPermissionTranslationsPublic } from './internal/get-app-permission-translations-public';
const translateAppPermissionDescriptions = (appPermissionNames, translations, ungatedForBETranslation, appObjectNames) => {
  const translationMap = new Map();
  appPermissionNames.forEach(name => {
    if (getIsAppObjectAppPermission(name)) {
      // app object app permissions are still translated by the FE
      const appObjectAppPermissionDescription = getAppObjectAppPermissionDescription(name, appObjectNames);
      translationMap.set(name, {
        name,
        ungatedForBETranslation,
        shortDescription: undefined,
        longDescription: appObjectAppPermissionDescription
      });
    } else {
      const translation = translations.get(name);
      translationMap.set(name, {
        name,
        ungatedForBETranslation,
        shortDescription: translation === null || translation === void 0 ? void 0 : translation.label,
        longDescription: translation === null || translation === void 0 ? void 0 : translation.description
      });
    }
  });
  return translationMap;
};
export const useAppPermissionTranslations = (appPermissionNames, ungatedForBETranslation) => {
  const names = useMemo(() => Array.from(appPermissionNames), [appPermissionNames]);
  const appObjectNames = useFetchAppObjectNames({
    names: getAppObjectFQNs(names)
  });
  const namesWithoutAppObjects = useMemo(() => names.filter(name => !getIsAppObjectAppPermission(name)), [names]);
  const translations = useGetAppPermissionTranslations(namesWithoutAppObjects);
  const translationsMap = useMemo(() => translateAppPermissionDescriptions(names, translations, ungatedForBETranslation, appObjectNames), [names, translations, appObjectNames, ungatedForBETranslation]);
  return translationsMap;
};
export const useAppPermissionTranslationsPublic = appPermissionNames => {
  const names = useMemo(() => Array.from(appPermissionNames), [appPermissionNames]);
  const namesWithoutAppObjects = useMemo(() => names.filter(name => !getIsAppObjectAppPermission(name)), [names]);
  const translations = useGetAppPermissionTranslationsPublic(namesWithoutAppObjects);
  const translationsMap = useMemo(() => translateAppPermissionDescriptions(names, translations, true, {}), [names, translations]);
  return translationsMap;
};
const getI18nAppPermissionDescriptionKey_FE = name => {
  return getScopeGroupI18nKey(name, 'description');
};
const getI18nAppPermissionDescription_FE = name => {
  const key = getI18nAppPermissionDescriptionKey_FE(name);
  return isValidI18nKey(key) ? I18n.text(key) : undefined;
};
export const __INTERNALS__ = {
  getI18nAppPermissionDescriptionKey_FE,
  getI18nAppPermissionDescription_FE
};
export const renderI18nAppPermissionDescription = props => {
  const {
    name,
    longDescription,
    shortDescription,
    translations
  } = props;

  // for app object app permissions, we use the `translations.longDescription` regardless of gate.
  // this is because app object app permissions translations are done at the previous step (requires fetching app objects)
  const translatedLongDescription = getIsAppObjectAppPermission(name) ? translations.longDescription : translations.ungatedForBETranslation ? translations.longDescription : getI18nAppPermissionDescription_FE(name);
  const translatedShortDescription = translations.shortDescription;
  const nonTranslated = longDescription || shortDescription || name;
  return translatedLongDescription || translatedShortDescription || nonTranslated;
};